
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import type { SpmColumnConfig, ApiCallConfig, ExportPayload } from '@/components/table-rest-api/types';
import {
  getChannelIcon as getChannelIconFromTokens,
  getChannelTagStyleVars,
} from '@/views/automated-scenarios/utils/styleTokens';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { defineComponent, ref } from 'vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { findPermission } from '@/composables/User';

export default defineComponent({
  name: 'ListsConsents',

  components: {
    SpmTableAPI,
    PageHeader,
    SpmBadge,
    SpmDateDisplay,
  },

  setup() {
    const { t } = useI18n();

    const childComponentRef = ref();

    const setLoading = async (loading: boolean) => {
      if (childComponentRef.value) {
        await childComponentRef.value.setLoading(loading);
      }
    };

    // API configuration
    const apiCall = ref<ApiCallConfig>({
      type_api: NestApiType.CLIENT_API,
      path: '/contact-consent-history',
      secure: true,
    });

    // Columns configuration
    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'fullname',
        header: t('consents.headers.fullName'),
        fields: [{
          name: 'first_name',
          label: t('consents.headers.firstname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'last_name',
          label: t('consents.headers.lastname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'email',
        header: t('consents.headers.email'),
        fields: [{
          name: 'email',
          label: t('consents.headers.email'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'sms',
        header: t('consents.headers.phone'),
        fields: [{
          name: 'phone',
          label: t('consents.headers.phone'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'push',
        header: t('consents.headers.push'),
        fields: ['push'],
      },
      {
        id: 'channel',
        header: t('consents.headers.channel'),
        fields: [{
          name: 'channel_consent',
          label: t('consents.headers.channel'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { label: t('consents.channels.email'), value: 1 },
              { label: t('consents.channels.sms'), value: 2 },
              { label: t('consents.channels.whatsapp'), value: 3 },
              { label: t('consents.channels.push'), value: 4 },
            ],
          },
        }],
      },
      {
        id: 'type',
        header: t('consents.headers.type'),
        fields: [{
          name: 'type_consent',
          label: t('consents.headers.type'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { label: t('consents.types.subscribe'), value: 'subscribe' },
              { label: t('consents.types.unsubscribe'), value: 'unsubscribe' },
              { label: t('consents.types.spam_compliant'), value: 'spam_compliant' },
            ],
          },
        }],
      },
      {
        id: 'created_at',
        header: t('consents.headers.createdAt'),
        fields: [{
          name: 'created_at_consent',
          label: t('consents.headers.createdAt'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
    ];

    /**
     * Get channel label
     */
    const getChannelLabel = (channel: number): string => {
      const channelMap: Record<number, string> = {
        1: t('consents.channels.email'),
        2: t('consents.channels.sms'),
        3: t('consents.channels.whatsapp'),
        4: t('consents.channels.push'),
      };
      return channelMap[channel] || '-';
    };

    // Map channel number to channel name for styleTokens
    const channelNumberToName: Record<number, string> = {
      1: 'email',
      2: 'sms',
      3: 'whatsapp',
      4: 'pushnotifications',
    };

    /**
     * Get channel icon using styleTokens
     */
    const getChannelIcon = (channel: number): string => {
      const channelName = channelNumberToName[channel] || '';
      return getChannelIconFromTokens(channelName) || 'fa-solid fa-question';
    };

    /**
     * Get channel colors for SpmBadge using styleTokens
     */
    const getChannelColors = (channel: number): { bg: string; color: string; borderColor?: string } => {
      const channelName = channelNumberToName[channel] || '';
      const vars = getChannelTagStyleVars(channelName);
      return {
        bg: vars['--channel-bg'] || 'var(--surface-200)',
        color: vars['--channel-color'] || 'var(--text-color)',
        borderColor: vars['--channel-border'],
      };
    };

    /**
     * Get type label
     */
    const getTypeLabel = (type: string): string => {
      const normalizedType = type?.toLowerCase() || '';
      const typeMap: Record<string, string> = {
        subscribe: t('consents.types.subscribe'),
        unsubscribe: t('consents.types.unsubscribe'),
        spam_complaint: t('consents.types.spam_compliant'),
        hard_bounce: t('consents.types.hard_bounce'),
        soft_bounce: t('consents.types.soft_bounce'),
      };
      return typeMap[normalizedType] || type;
    };

    /**
     * Get type colors for SpmBadge
     */
    const getTypeColors = (type: string): { bg: string; color: string } => {
      const normalizedType = type?.toLowerCase() || '';
      const colorMap: Record<string, { bg: string; color: string }> = {
        subscribe: { bg: 'var(--green-100)', color: 'var(--green-700)' },
        unsubscribe: { bg: 'var(--red-100)', color: 'var(--red-700)' },
        spam_complaint: { bg: 'var(--orange-100)', color: 'var(--orange-700)' },
        hard_bounce: { bg: 'var(--red-100)', color: 'var(--red-700)' },
        soft_bounce: { bg: 'var(--yellow-100)', color: 'var(--yellow-700)' },
      };
      return colorMap[normalizedType] || { bg: 'var(--gray-100)', color: 'var(--gray-700)' };
    };

    // Helper to clean null/undefined strings
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    // Helper to format full name
    const formatFullName = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
      const first = cleanStr(firstName);
      const last = cleanStr(lastName);
      if (!first && !last) return '';
      return `${first} ${last}`.trim();
    };

    // Method to format phone numbers
    const formatPhoneNumber = (phone: string) => {
      if (!phone || phone === 'null') return '';
      // Remove all non-digit characters except +
      const cleaned = phone.replace(/[^\d+]/g, '');
      // Format with spaces every 2 digits for readability
      if (cleaned.startsWith('+')) {
        const countryCode = cleaned.slice(0, 3);
        const rest = cleaned.slice(3);
        return `${countryCode} ${rest.replace(/(\d{2})(?=\d)/g, '$1 ').trim()}`;
      }
      return cleaned.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        setLoading(true);
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          selection_type: payload.selectAllMode ? 'all' : 'manual',
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/export/init',
          {},
          '',
          exportRequest,
          true,
        );

        if (response && !response.error) {
          await showToastSuccess(t('myLists.manage.customers.export.success'));
        } else {
          await showToastError(t('myLists.manage.customers.export.error'));
        }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myLists.manage.customers.export.error'));
      } finally {
        setLoading(false);
      }
    };

    return {
      t,
      childComponentRef,
      apiCall,
      columns,
      getChannelLabel,
      getChannelIcon,
      getChannelColors,
      getTypeLabel,
      getTypeColors,
      formatFullName,
      cleanStr,
      formatPhoneNumber,
      handleExport,
      findPermission,
    };
  },
});
